// ─── Component Contracts ─────────────────────────────────────────────────────
//
// Platform-owned component capability authority for Phase 3.
// Defines what every component type is ALLOWED to declare, emit, bind, and access.
//
// ComponentCapabilityContract is a Platform Fact - invariant across all
// AppSchema boots on the same platform version.
//
// Authority model:
//   initializeContractRegistry() is the authority creator (idempotent).
//   boot() calls initializeContractRegistry() as a verification guard.
//   requireContract() is the consumer API.
//
// Validation:
//   V-1  through V-20 - structural invariants (checked at registration)
//   V-21 through V-26 - evolution invariants (checked at version update)
//
// Import path: @/components/componentContracts

import type { ActionType } from '@/core/schema/actionSchema';
import type { BindingSourceType, BindingTargetType } from '@/core/schema/bindingSchema';
import type { StateScope } from '@/core/schema/stateSchema';
import { logger } from '@/system/logger';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ComponentFunctionalCategory =
  | 'data'
  | 'input'
  | 'layout'
  | 'navigation'
  | 'visualization'
  | 'feedback';

export type PageCompatibilityMode = 'marketing' | 'app';

export interface ComponentCapabilityContract {
  /** Unique component type identifier - matches SchemaComponent.type */
  type: string;
  /** Human-readable display name */
  name: string;
  /** Semver contract version */
  version: string;
  /** Functional category - matches AppComponentContract.category */
  componentCategory: ComponentFunctionalCategory;
  /** Which page modes this component is compatible with */
  pageCompatibility: PageCompatibilityMode[];

  /** Dimension 1 - Event capabilities */
  events: {
    emits: string[];
    receives: string[];
  };

  /** Dimension 2 - Action capabilities */
  actions: {
    canTrigger: ActionType[];
    canReceive: ActionType[];
  };

  /** Dimension 3 - Data capabilities */
  data: {
    collectionBinding: boolean;
    reads: boolean;
    writes: boolean;
    allowedSources: BindingSourceType[];
    requiredFields?: string[];
  };

  /** Dimension 4 - State capabilities */
  state: {
    reads: string[];
    writes: string[];
    allowedScopes: StateScope[];
  };

  /** Dimension 5 - Binding capabilities */
  bindings: {
    allowedSources: BindingSourceType[];
    allowedTargets: BindingTargetType[];
    maxBindingsPerTarget: number;
  };

  /** Dimension 6 - Structural capabilities */
  structural: {
    canContainChildren: boolean;
    allowedChildTypes?: string[];
    requiredProps: string[];
    optionalProps: Record<string, unknown>;
    maxChildren?: number;
  };
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// ─── Error Classes ────────────────────────────────────────────────────────────

export class ContractRegistrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ContractRegistrationError';
  }
}

export class ContractValidationError extends Error {
  constructor(
    public readonly contractType: string,
    public readonly violations: string[],
  ) {
    super(`Contract validation failed for "${contractType}": ${violations.join('; ')}`);
    this.name = 'ContractValidationError';
  }
}

export class ContractEvolutionError extends Error {
  constructor(
    public readonly contractType: string,
    public readonly violations: string[],
  ) {
    super(`Contract evolution violation for "${contractType}": ${violations.join('; ')}`);
    this.name = 'ContractEvolutionError';
  }
}

export class ContractNotFoundError extends Error {
  constructor(public readonly contractType: string) {
    super(`Component contract not found: "${contractType}"`);
    this.name = 'ContractNotFoundError';
  }
}

// ─── Validation Sets ──────────────────────────────────────────────────────────

const VALID_FUNCTIONAL_CATEGORIES: ReadonlySet<string> = new Set([
  'data', 'input', 'layout', 'navigation', 'visualization', 'feedback',
]);

const VALID_PAGE_MODES: ReadonlySet<string> = new Set(['marketing', 'app']);

const VALID_ACTION_TYPES: ReadonlySet<string> = new Set([
  'set_state', 'toggle_state', 'insert_record', 'update_record', 'delete_record',
  'navigate', 'validate_form', 'show_notification', 'chain', 'conditional',
  'switch_case', 'loop', 'guard', 'try_catch', 'retry', 'emit_event', 'parallel',
  'filter_collection', 'reset_state', 'api_call', 'delay', 'batch',
]);

const VALID_BINDING_SOURCE_TYPES: ReadonlySet<string> = new Set([
  'state', 'data_query', 'static', 'computed',
]);

const VALID_BINDING_TARGET_TYPES: ReadonlySet<string> = new Set([
  'prop', 'style', 'visibility', 'enabled', 'text',
]);

const VALID_STATE_SCOPES: ReadonlySet<string> = new Set([
  'global', 'page', 'component',
]);

// ─── Semver Helpers ───────────────────────────────────────────────────────────

function isValidSemver(v: string): boolean {
  return /^\d+\.\d+\.\d+$/.test(v);
}

function compareSemver(a: string, b: string): number {
  const [aMaj = 0, aMin = 0, aPatch = 0] = a.split('.').map(Number);
  const [bMaj = 0, bMin = 0, bPatch = 0] = b.split('.').map(Number);
  if (aMaj !== bMaj) return aMaj - bMaj;
  if (aMin !== bMin) return aMin - bMin;
  return aPatch - bPatch;
}

// ─── Structural Validation (V-1 through V-20) ─────────────────────────────────

/**
 * Validates a ComponentCapabilityContract against all 20 structural invariants.
 * Collects all violations (does not short-circuit on first failure).
 */
export function validateContract(contract: ComponentCapabilityContract): ValidationResult {
  const errors: string[] = [];

  // V-1: type is non-empty string
  if (typeof contract.type !== 'string' || contract.type.trim() === '') {
    errors.push('V-1: type must be a non-empty string');
  }

  // V-2: name is non-empty string
  if (typeof contract.name !== 'string' || contract.name.trim() === '') {
    errors.push('V-2: name must be a non-empty string');
  }

  // V-3: version is valid semver
  if (typeof contract.version !== 'string' || !isValidSemver(contract.version)) {
    errors.push('V-3: version must be valid semver (major.minor.patch)');
  }

  // V-4: componentCategory is valid ComponentFunctionalCategory
  if (!VALID_FUNCTIONAL_CATEGORIES.has(contract.componentCategory)) {
    errors.push(`V-4: componentCategory must be one of: ${[...VALID_FUNCTIONAL_CATEGORIES].join(', ')}`);
  }

  // V-5: pageCompatibility is non-empty array
  if (!Array.isArray(contract.pageCompatibility) || contract.pageCompatibility.length === 0) {
    errors.push('V-5: pageCompatibility must be a non-empty array');
  }

  // V-6: each pageCompatibility value is valid PageCompatibilityMode
  if (Array.isArray(contract.pageCompatibility)) {
    const invalid = contract.pageCompatibility.filter(m => !VALID_PAGE_MODES.has(m as string));
    if (invalid.length > 0) {
      errors.push(`V-6: invalid pageCompatibility values: ${invalid.join(', ')}`);
    }
  }

  // V-7: events.emits contains only non-empty strings
  if (!Array.isArray(contract.events.emits)) {
    errors.push('V-7: events.emits must be an array');
  } else if (contract.events.emits.some(e => typeof e !== 'string' || e.trim() === '')) {
    errors.push('V-7: events.emits must contain only non-empty strings');
  }

  // V-8: events.receives contains only non-empty strings
  if (!Array.isArray(contract.events.receives)) {
    errors.push('V-8: events.receives must be an array');
  } else if (contract.events.receives.some(e => typeof e !== 'string' || e.trim() === '')) {
    errors.push('V-8: events.receives must contain only non-empty strings');
  }

  // V-9: actions.canTrigger contains only valid ActionType values
  if (!Array.isArray(contract.actions.canTrigger)) {
    errors.push('V-9: actions.canTrigger must be an array');
  } else {
    const invalid = contract.actions.canTrigger.filter(a => !VALID_ACTION_TYPES.has(a as string));
    if (invalid.length > 0) {
      errors.push(`V-9: invalid ActionType values in canTrigger: ${invalid.join(', ')}`);
    }
  }

  // V-10: actions.canReceive contains only valid ActionType values
  if (!Array.isArray(contract.actions.canReceive)) {
    errors.push('V-10: actions.canReceive must be an array');
  } else {
    const invalid = contract.actions.canReceive.filter(a => !VALID_ACTION_TYPES.has(a as string));
    if (invalid.length > 0) {
      errors.push(`V-10: invalid ActionType values in canReceive: ${invalid.join(', ')}`);
    }
  }

  // V-11: data.collectionBinding is boolean
  if (typeof contract.data.collectionBinding !== 'boolean') {
    errors.push('V-11: data.collectionBinding must be a boolean');
  }

  // V-12: data.reads is boolean
  if (typeof contract.data.reads !== 'boolean') {
    errors.push('V-12: data.reads must be a boolean');
  }

  // V-13: data.writes is boolean
  if (typeof contract.data.writes !== 'boolean') {
    errors.push('V-13: data.writes must be a boolean');
  }

  // V-14: data.allowedSources contains only valid BindingSourceType values
  if (!Array.isArray(contract.data.allowedSources)) {
    errors.push('V-14: data.allowedSources must be an array');
  } else {
    const invalid = contract.data.allowedSources.filter(s => !VALID_BINDING_SOURCE_TYPES.has(s as string));
    if (invalid.length > 0) {
      errors.push(`V-14: invalid BindingSourceType in data.allowedSources: ${invalid.join(', ')}`);
    }
  }

  // V-15: state.reads contains only non-empty strings
  if (!Array.isArray(contract.state.reads)) {
    errors.push('V-15: state.reads must be an array');
  } else if (contract.state.reads.some(s => typeof s !== 'string' || s.trim() === '')) {
    errors.push('V-15: state.reads must contain only non-empty strings');
  }

  // V-16: state.writes contains only non-empty strings
  if (!Array.isArray(contract.state.writes)) {
    errors.push('V-16: state.writes must be an array');
  } else if (contract.state.writes.some(s => typeof s !== 'string' || s.trim() === '')) {
    errors.push('V-16: state.writes must contain only non-empty strings');
  }

  // V-17: state.allowedScopes contains only valid StateScope values
  if (!Array.isArray(contract.state.allowedScopes)) {
    errors.push('V-17: state.allowedScopes must be an array');
  } else {
    const invalid = contract.state.allowedScopes.filter(s => !VALID_STATE_SCOPES.has(s as string));
    if (invalid.length > 0) {
      errors.push(`V-17: invalid StateScope values: ${invalid.join(', ')}`);
    }
  }

  // V-18: bindings.allowedSources contains only valid BindingSourceType values
  if (!Array.isArray(contract.bindings.allowedSources)) {
    errors.push('V-18: bindings.allowedSources must be an array');
  } else {
    const invalid = contract.bindings.allowedSources.filter(s => !VALID_BINDING_SOURCE_TYPES.has(s as string));
    if (invalid.length > 0) {
      errors.push(`V-18: invalid BindingSourceType in bindings.allowedSources: ${invalid.join(', ')}`);
    }
  }

  // V-19: bindings.allowedTargets contains only valid BindingTargetType values
  if (!Array.isArray(contract.bindings.allowedTargets)) {
    errors.push('V-19: bindings.allowedTargets must be an array');
  } else {
    const invalid = contract.bindings.allowedTargets.filter(t => !VALID_BINDING_TARGET_TYPES.has(t as string));
    if (invalid.length > 0) {
      errors.push(`V-19: invalid BindingTargetType values: ${invalid.join(', ')}`);
    }
  }

  // V-20: bindings.maxBindingsPerTarget is a positive integer >= 1
  if (
    typeof contract.bindings.maxBindingsPerTarget !== 'number' ||
    !Number.isInteger(contract.bindings.maxBindingsPerTarget) ||
    contract.bindings.maxBindingsPerTarget < 1
  ) {
    errors.push('V-20: bindings.maxBindingsPerTarget must be a positive integer (>= 1)');
  }

  return { valid: errors.length === 0, errors };
}

// ─── Evolution Validation (V-21 through V-26) ────────────────────────────────

/**
 * Validates that a new contract version is backward-compatible with the existing one.
 * Called before updating a contract that is already in the registry.
 */
export function validateContractEvolution(
  existing: ComponentCapabilityContract,
  next: ComponentCapabilityContract,
): ValidationResult {
  const errors: string[] = [];

  // V-21: version must be strictly greater than existing version
  if (!isValidSemver(next.version) || compareSemver(next.version, existing.version) <= 0) {
    errors.push(
      `V-21: next version "${next.version}" must be strictly greater than existing "${existing.version}"`
    );
  }

  // V-22: existing events.emits must be subset of new events.emits (no removal)
  const nextEmits = new Set(next.events.emits);
  const removedEmits = existing.events.emits.filter(e => !nextEmits.has(e));
  if (removedEmits.length > 0) {
    errors.push(`V-22: events.emits may not be removed: ${removedEmits.join(', ')}`);
  }

  // V-23: existing events.receives must be subset of new events.receives (no removal)
  const nextReceives = new Set(next.events.receives);
  const removedReceives = existing.events.receives.filter(e => !nextReceives.has(e));
  if (removedReceives.length > 0) {
    errors.push(`V-23: events.receives may not be removed: ${removedReceives.join(', ')}`);
  }

  // V-24: existing data.allowedSources must be subset of new data.allowedSources (no removal)
  const nextDataSources = new Set(next.data.allowedSources);
  const removedDataSources = existing.data.allowedSources.filter(s => !nextDataSources.has(s));
  if (removedDataSources.length > 0) {
    errors.push(`V-24: data.allowedSources may not be removed: ${removedDataSources.join(', ')}`);
  }

  // V-25: existing bindings.allowedTargets must be subset of new bindings.allowedTargets (no removal)
  const nextTargets = new Set(next.bindings.allowedTargets);
  const removedTargets = existing.bindings.allowedTargets.filter(t => !nextTargets.has(t));
  if (removedTargets.length > 0) {
    errors.push(`V-25: bindings.allowedTargets may not be removed: ${removedTargets.join(', ')}`);
  }

  // V-26: new structural.requiredProps must be subset of existing (cannot add new required props)
  const existingRequired = new Set(existing.structural.requiredProps);
  const addedRequired = next.structural.requiredProps.filter(p => !existingRequired.has(p));
  if (addedRequired.length > 0) {
    errors.push(`V-26: structural.requiredProps may not gain new entries: ${addedRequired.join(', ')}`);
  }

  return { valid: errors.length === 0, errors };
}

// ─── Registry ─────────────────────────────────────────────────────────────────

const _registry = new Map<string, ComponentCapabilityContract>();
let _initialized = false;

// ─── Public Registry API ──────────────────────────────────────────────────────

/**
 * Returns the contract for the given component type.
 * Throws ContractNotFoundError if not registered.
 */
export function requireContract(type: string): ComponentCapabilityContract {
  const contract = _registry.get(type);
  if (!contract) throw new ContractNotFoundError(type);
  return contract;
}

/** Returns the contract for the given type, or undefined if not registered. */
export function getContract(type: string): ComponentCapabilityContract | undefined {
  return _registry.get(type);
}

/** All currently registered contracts. */
export function getAllContracts(): ComponentCapabilityContract[] {
  return [..._registry.values()];
}

/** Whether a contract is registered for the given type. */
export function hasContract(type: string): boolean {
  return _registry.has(type);
}

/**
 * Clears the registry and resets initialization state.
 * For test isolation only.
 */
export function clearContracts(): void {
  _registry.clear();
  _initialized = false;
}

/**
 * Validates and registers a new contract.
 * Throws ContractValidationError (V-1-V-20) or ContractRegistrationError if duplicate.
 */
export function registerContract(contract: ComponentCapabilityContract): void {
  if (_registry.has(contract.type)) {
    throw new ContractRegistrationError(
      `Contract already registered for type "${contract.type}". Use updateContract() to evolve it.`
    );
  }
  const result = validateContract(contract);
  if (!result.valid) {
    throw new ContractValidationError(contract.type, result.errors);
  }
  _registry.set(contract.type, contract);
}

/**
 * Updates an existing contract with a new version.
 * Validates V-1-V-20 (structural) and V-21-V-26 (evolution).
 * Throws ContractValidationError, ContractEvolutionError, or ContractRegistrationError.
 */
export function updateContract(contract: ComponentCapabilityContract): void {
  const existing = _registry.get(contract.type);
  if (!existing) {
    throw new ContractRegistrationError(
      `No contract registered for type "${contract.type}". Use registerContract() for new types.`
    );
  }
  const structResult = validateContract(contract);
  if (!structResult.valid) {
    throw new ContractValidationError(contract.type, structResult.errors);
  }
  const evoResult = validateContractEvolution(existing, contract);
  if (!evoResult.valid) {
    throw new ContractEvolutionError(contract.type, evoResult.errors);
  }
  _registry.set(contract.type, contract);
}

// ─── Core Contract Definitions ────────────────────────────────────────────────

const CORE_CONTRACTS: ComponentCapabilityContract[] = [

  // ─── Data Components ───────────────────────────────────────────────────────

  {
    type: 'data_table',
    name: 'Data Table',
    version: '1.0.0',
    componentCategory: 'data',
    pageCompatibility: ['app'],
    events: { emits: ['row_click', 'row_select', 'sort', 'page_change'], receives: ['refresh', 'filter'] },
    actions: { canTrigger: ['set_state', 'navigate', 'show_notification', 'delete_record', 'chain', 'conditional'], canReceive: [] },
    data: { collectionBinding: true, reads: true, writes: false, allowedSources: ['data_query'] },
    state: { reads: ['filterQuery', 'selectedItem'], writes: ['selectedItem'], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'data_query', 'static'], allowedTargets: ['prop', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['columns'], optionalProps: { pageSize: 10, sortable: true, selectable: false, searchable: true } },
  },

  {
    type: 'list_view',
    name: 'List View',
    version: '1.0.0',
    componentCategory: 'data',
    pageCompatibility: ['app'],
    events: { emits: ['item_click', 'item_select'], receives: ['refresh', 'filter'] },
    actions: { canTrigger: ['set_state', 'navigate', 'show_notification', 'chain', 'conditional'], canReceive: [] },
    data: { collectionBinding: true, reads: true, writes: false, allowedSources: ['data_query'] },
    state: { reads: ['filterQuery'], writes: ['selectedItem'], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'data_query', 'static'], allowedTargets: ['prop', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['titleField'], optionalProps: { subtitleField: null, avatarField: null, layout: 'list' } },
  },

  {
    type: 'detail_view',
    name: 'Detail View',
    version: '1.0.0',
    componentCategory: 'data',
    pageCompatibility: ['app'],
    events: { emits: ['edit', 'delete', 'back'], receives: ['refresh'] },
    actions: { canTrigger: ['set_state', 'navigate', 'show_notification', 'delete_record', 'chain', 'conditional'], canReceive: [] },
    data: { collectionBinding: true, reads: true, writes: false, allowedSources: ['data_query'] },
    state: { reads: ['selectedItem'], writes: [], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'data_query', 'static'], allowedTargets: ['prop', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['fields'], optionalProps: { editable: false, showActions: true } },
  },

  // ─── Input Components ──────────────────────────────────────────────────────

  {
    type: 'form',
    name: 'Form',
    version: '1.0.0',
    componentCategory: 'input',
    pageCompatibility: ['marketing', 'app'],
    events: { emits: ['submit', 'reset', 'validate'], receives: ['clear', 'fill'] },
    actions: { canTrigger: ['set_state', 'navigate', 'show_notification', 'insert_record', 'update_record', 'validate_form', 'chain', 'conditional'], canReceive: [] },
    data: { collectionBinding: true, reads: false, writes: true, allowedSources: ['static', 'state'] },
    state: { reads: [], writes: ['formData'], allowedScopes: ['global', 'page', 'component'] },
    bindings: { allowedSources: ['state', 'static', 'computed'], allowedTargets: ['prop', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['fields'], optionalProps: { submitLabel: 'Submit', resetLabel: 'Reset', layout: 'vertical' } },
  },

  {
    type: 'filters',
    name: 'Filter Bar',
    version: '1.0.0',
    componentCategory: 'input',
    pageCompatibility: ['app'],
    events: { emits: ['filter_change', 'clear'], receives: ['reset'] },
    actions: { canTrigger: ['set_state', 'chain'], canReceive: [] },
    data: { collectionBinding: true, reads: true, writes: false, allowedSources: ['data_query', 'static'] },
    state: { reads: [], writes: ['filterQuery'], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'static'], allowedTargets: ['prop', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['filterFields'], optionalProps: { layout: 'horizontal', showClear: true } },
  },

  {
    type: 'search',
    name: 'Search Bar',
    version: '1.0.0',
    componentCategory: 'input',
    pageCompatibility: ['marketing', 'app'],
    events: { emits: ['search', 'clear'], receives: [] },
    actions: { canTrigger: ['set_state', 'chain'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: [] },
    state: { reads: [], writes: ['filterQuery'], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'static'], allowedTargets: ['prop', 'visibility', 'enabled', 'text'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: [], optionalProps: { placeholder: 'Search...', debounceMs: 300 } },
  },

  {
    type: 'auth_form',
    name: 'Auth Form',
    version: '1.0.0',
    componentCategory: 'input',
    pageCompatibility: ['app'],
    events: { emits: ['login', 'signup', 'forgot_password'], receives: [] },
    actions: { canTrigger: ['set_state', 'navigate', 'show_notification', 'chain', 'conditional'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: [] },
    state: { reads: [], writes: ['formData'], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'static'], allowedTargets: ['prop', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['mode'], optionalProps: { mode: 'login', socialProviders: [], showForgotPassword: true } },
  },

  // ─── Navigation Components ─────────────────────────────────────────────────

  {
    // Global navigation / footer / sidebar chrome. The planner (planToSchema) maps
    // navigation, footer, and sidebar sections to this single type on both
    // marketing and app pages. It navigates between pages and carries no required
    // props (its content is the page set), so the runtime can register + render it.
    type: 'navigation',
    name: 'Navigation',
    version: '1.0.0',
    componentCategory: 'navigation',
    pageCompatibility: ['marketing', 'app'],
    events: { emits: ['navigate', 'click'], receives: [] },
    actions: { canTrigger: ['navigate', 'set_state', 'chain'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: [] },
    state: { reads: [], writes: [], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'static'], allowedTargets: ['prop', 'visibility', 'enabled', 'text'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: [], optionalProps: { purpose: '', contentGoal: '', variant: 'top', links: [] } },
  },

  {
    // Generic page-content block. The planner emits a "custom" section for the
    // primary content of pages that have no specific archetype (planningReasoning
    // default case); the runtime renders it via LiveCustomComponent. Without this
    // contract the R-1 registration gate rejects the component and the live
    // preview fails to boot, so a real generated app (e.g. a schedule page)
    // silently breaks. Benign by design: no required props, no data binding.
    type: 'custom',
    name: 'Custom Content',
    version: '1.0.0',
    componentCategory: 'layout',
    pageCompatibility: ['marketing', 'app'],
    events: { emits: ['click'], receives: [] },
    actions: { canTrigger: ['navigate', 'set_state', 'show_notification', 'chain', 'conditional'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: [] },
    state: { reads: [], writes: [], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'static'], allowedTargets: ['prop', 'visibility', 'enabled', 'text'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: [], optionalProps: { purpose: '', contentGoal: '', heading: '', body: '' } },
  },

  // ─── Visualization Components ──────────────────────────────────────────────

  {
    type: 'chart',
    name: 'Chart',
    version: '1.0.0',
    componentCategory: 'visualization',
    pageCompatibility: ['app'],
    events: { emits: ['data_point_click'], receives: ['refresh'] },
    actions: { canTrigger: ['set_state', 'navigate', 'chain'], canReceive: [] },
    data: { collectionBinding: true, reads: true, writes: false, allowedSources: ['data_query'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['state', 'data_query', 'static'], allowedTargets: ['prop', 'visibility'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['chartType', 'xField', 'yField'], optionalProps: { chartType: 'bar', colors: null, showLegend: true } },
  },

  {
    type: 'dashboard_overview',
    name: 'Dashboard Overview',
    version: '1.0.0',
    componentCategory: 'visualization',
    pageCompatibility: ['app'],
    events: { emits: ['card_click'], receives: ['refresh'] },
    actions: { canTrigger: ['set_state', 'navigate', 'chain'], canReceive: [] },
    data: { collectionBinding: false, reads: true, writes: false, allowedSources: ['state', 'static'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['state', 'data_query', 'static'], allowedTargets: ['prop', 'visibility'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['metrics'], optionalProps: { columns: 4, showTrend: true } },
  },

  {
    type: 'stats',
    name: 'Stat Cards',
    version: '1.0.0',
    componentCategory: 'visualization',
    pageCompatibility: ['marketing', 'app'],
    events: { emits: [], receives: [] },
    actions: { canTrigger: [], canReceive: [] },
    data: { collectionBinding: false, reads: true, writes: false, allowedSources: ['state', 'static'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['state', 'static'], allowedTargets: ['prop', 'visibility', 'text'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['stats'], optionalProps: { layout: 'row', showIcon: true } },
  },

  // ─── Feedback Components ───────────────────────────────────────────────────

  {
    type: 'empty_state',
    name: 'Empty State',
    version: '1.0.0',
    componentCategory: 'feedback',
    pageCompatibility: ['app'],
    events: { emits: ['create_first'], receives: [] },
    actions: { canTrigger: ['set_state', 'navigate', 'show_notification', 'chain'], canReceive: [] },
    data: { collectionBinding: true, reads: true, writes: false, allowedSources: ['data_query'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['state', 'static'], allowedTargets: ['prop', 'visibility', 'text'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['title', 'description'], optionalProps: { icon: null, actionLabel: 'Create First' } },
  },

  // ─── Layout Components ─────────────────────────────────────────────────────

  {
    type: 'onboarding',
    name: 'Onboarding Wizard',
    version: '1.0.0',
    componentCategory: 'layout',
    pageCompatibility: ['app'],
    events: { emits: ['step_complete', 'wizard_complete', 'skip'], receives: [] },
    actions: { canTrigger: ['set_state', 'navigate', 'show_notification', 'chain', 'conditional'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: [] },
    state: { reads: [], writes: ['onboardingStep'], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'static'], allowedTargets: ['prop', 'visibility'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['steps'], optionalProps: { showProgress: true, allowSkip: true } },
  },

  // ─── Marketing Components ──────────────────────────────────────────────────

  {
    type: 'hero',
    name: 'Hero Section',
    version: '1.0.0',
    componentCategory: 'layout',
    pageCompatibility: ['marketing'],
    events: { emits: ['cta_click'], receives: [] },
    actions: { canTrigger: ['navigate', 'show_notification', 'chain'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: ['static'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['static', 'state'], allowedTargets: ['prop', 'text', 'visibility'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['headline', 'ctaLabel'], optionalProps: { subheadline: null, backgroundImage: null } },
  },

  {
    type: 'cta',
    name: 'Call to Action',
    version: '1.0.0',
    componentCategory: 'layout',
    pageCompatibility: ['marketing', 'app'],
    events: { emits: ['click'], receives: [] },
    actions: { canTrigger: ['navigate', 'set_state', 'show_notification', 'chain'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: ['static'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['static', 'state'], allowedTargets: ['prop', 'text', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['label'], optionalProps: { variant: 'primary', size: 'md' } },
  },

  {
    type: 'testimonials',
    name: 'Testimonials',
    version: '1.0.0',
    componentCategory: 'layout',
    pageCompatibility: ['marketing'],
    events: { emits: [], receives: [] },
    actions: { canTrigger: [], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: ['static'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['static'], allowedTargets: ['prop', 'visibility'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['items'], optionalProps: { layout: 'grid', autoPlay: false } },
  },

  {
    type: 'social_proof',
    name: 'Social Proof',
    version: '1.0.0',
    componentCategory: 'layout',
    pageCompatibility: ['marketing'],
    events: { emits: [], receives: [] },
    actions: { canTrigger: [], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: ['static'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['static'], allowedTargets: ['prop', 'visibility'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['logos'], optionalProps: { label: null } },
  },

  {
    type: 'pricing',
    name: 'Pricing',
    version: '1.0.0',
    componentCategory: 'layout',
    pageCompatibility: ['marketing'],
    events: { emits: ['plan_select'], receives: [] },
    actions: { canTrigger: ['navigate', 'set_state', 'chain'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: ['static'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['static', 'state'], allowedTargets: ['prop', 'visibility'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['plans'], optionalProps: { highlightedPlan: null, showAnnual: false } },
  },

  {
    type: 'faq',
    name: 'FAQ',
    version: '1.0.0',
    componentCategory: 'layout',
    pageCompatibility: ['marketing'],
    events: { emits: [], receives: [] },
    actions: { canTrigger: [], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: ['static'] },
    state: { reads: [], writes: [], allowedScopes: [] },
    bindings: { allowedSources: ['static'], allowedTargets: ['prop', 'visibility'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['items'], optionalProps: { expandAll: false } },
  },

  {
    type: 'newsletter',
    name: 'Newsletter',
    version: '1.0.0',
    componentCategory: 'input',
    pageCompatibility: ['marketing'],
    events: { emits: ['subscribe'], receives: [] },
    actions: { canTrigger: ['set_state', 'show_notification', 'chain'], canReceive: [] },
    data: { collectionBinding: false, reads: false, writes: false, allowedSources: ['static'] },
    state: { reads: [], writes: ['formData'], allowedScopes: ['page', 'component'] },
    bindings: { allowedSources: ['static', 'state'], allowedTargets: ['prop', 'text', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: [], optionalProps: { placeholder: 'Enter your email', buttonLabel: 'Subscribe' } },
  },

  // ─── Workflow Components ───────────────────────────────────────────────────

  {
    type: 'workflow_view',
    name: 'Workflow Viewer',
    version: '1.0.0',
    componentCategory: 'visualization',
    pageCompatibility: ['app'],
    events: { emits: ['step_click', 'workflow_complete', 'workflow_error'], receives: ['refresh', 'reset'] },
    actions: { canTrigger: ['set_state', 'navigate', 'show_notification', 'chain', 'conditional'], canReceive: [] },
    data: { collectionBinding: false, reads: true, writes: false, allowedSources: ['state', 'static'] },
    state: { reads: ['currentStep', 'workflowStatus'], writes: ['currentStep'], allowedScopes: ['global', 'page'] },
    bindings: { allowedSources: ['state', 'static', 'computed'], allowedTargets: ['prop', 'visibility', 'enabled'], maxBindingsPerTarget: 1 },
    structural: { canContainChildren: false, requiredProps: ['workflowId'], optionalProps: { showProgress: true, showStepLabels: true, orientation: 'horizontal' } },
  },

];

/**
 * The component types that ship with a core capability contract, as a static set
 * (derived at module load, no registry initialization required). Generation code
 * uses this to guarantee it never emits a component type the R-1 registration gate
 * would reject, which would fail the live-preview boot.
 */
export const CORE_CONTRACT_TYPES: ReadonlySet<string> = new Set(CORE_CONTRACTS.map(c => c.type));

// ─── Initialization ───────────────────────────────────────────────────────────

/**
 * Ensures the contract registry is populated with all core component contracts.
 *
 * Idempotent: subsequent calls are no-ops. On the first call, validates and
 * registers all core contracts atomically. Throws ContractRegistrationError if
 * any core contract fails validation (indicates a platform defect).
 *
 * Called as a verification guard inside AppRuntimePhase3.boot() and may be
 * called independently by Blueprint Intelligence, Marketplace Validation,
 * Plugin Loading, and Schema Migration.
 */
export function initializeContractRegistry(): void {
  if (_initialized) {
    logger.debug('initializeContractRegistry: already initialized');
    return;
  }

  logger.debug('initializeContractRegistry: registering core contracts');

  try {
    for (const contract of CORE_CONTRACTS) {
      const result = validateContract(contract);
      if (!result.valid) {
        throw new ContractRegistrationError(
          `Core contract "${contract.type}" failed validation: ${result.errors.join('; ')}`
        );
      }
      _registry.set(contract.type, contract);
    }
    _initialized = true;
    logger.debug(`initializeContractRegistry: registered ${_registry.size} contracts`);
  } catch (e) {
    _registry.clear();
    throw e;
  }
}
